import React from "react";
import SortableList, { RowTools } from "./SortableList";
import ChipsInput from "./ChipsInput";
import WeightMeter from "./WeightMeter";
import {
  SCALE_PRESETS,
  SECTION_TYPE_LABEL,
  itemWeightTotal,
  newColumn,
  newItem,
  sanitizeKey,
  scaleLabels,
  withTitle,
} from "../templateUtils";

const numValue = (v) => (v === "" ? "" : Number(v));

/** Key input: generated from the title, editable until the first save. */
const KeyField = ({ id, element, onChange, small }) => (
  <div>
    <label htmlFor={id} className="profile-label">
      Key
    </label>
    <input
      id={id}
      className={`form-control font-monospace ${small ? "form-control-sm" : ""}`}
      value={element.key}
      disabled={element._saved}
      onChange={(e) =>
        onChange({
          ...element,
          key: sanitizeKey(e.target.value),
          _keyEdited: true,
        })
      }
      title={
        element._saved
          ? "Keys can't change after the first save"
          : "Lowercase letters, digits, - and _"
      }
    />
  </div>
);

const Toggle = ({ id, checked, onChange, label }) => (
  <div className="form-check form-switch">
    <input
      id={id}
      type="checkbox"
      className="form-check-input"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    <label htmlFor={id} className="form-check-label fs-085">
      {label}
    </label>
  </div>
);

/* ---------- RATING ---------- */
const ScaleEditor = ({ section, set, readOnly }) => {
  const labels = scaleLabels(section.scale);
  const setScale = (changes) => {
    const scale = { ...section.scale, ...changes };
    set({ scale: { ...scale, labels: scaleLabels(scale) } });
  };
  return (
    <div className="builder-subcard">
      <div className="d-flex flex-wrap align-items-end gap-3 mb-2">
        <div>
          <label htmlFor={`${section._id}-min`} className="profile-label">
            Scale min
          </label>
          <input
            id={`${section._id}-min`}
            type="number"
            className="form-control form-control-sm w-80px"
            value={section.scale.min}
            onChange={(e) => setScale({ min: numValue(e.target.value) })}
          />
        </div>
        <div>
          <label htmlFor={`${section._id}-max`} className="profile-label">
            Scale max
          </label>
          <input
            id={`${section._id}-max`}
            type="number"
            className="form-control form-control-sm w-80px"
            value={section.scale.max}
            onChange={(e) => setScale({ max: numValue(e.target.value) })}
          />
        </div>
        {!readOnly && (
          <div className="d-flex gap-1 ms-auto">
            <span className="fs-075 text-slate-500 align-self-center me-1">
              Presets:
            </span>
            {Object.entries(SCALE_PRESETS).map(([name, preset]) => (
              <button
                key={name}
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() =>
                  set({ scale: JSON.parse(JSON.stringify(preset)) })
                }
              >
                {name.replace("-", "–")}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="scale-labels">
        {labels.map((l) => (
          <div key={l.value} className="scale-label-row">
            <span className="scale-value">{l.value}</span>
            <input
              className="form-control form-control-sm"
              value={l.label}
              placeholder="Label (optional)"
              aria-label={`Label for ${l.value}`}
              onChange={(e) =>
                set({
                  scale: {
                    ...section.scale,
                    labels: labels.map((x) =>
                      x.value === l.value ? { ...x, label: e.target.value } : x,
                    ),
                  },
                })
              }
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const RatingItems = ({ section, set, readOnly }) => {
  const custom = section.itemWeighting === "CUSTOM";
  const setItems = (items) => set({ items });
  const setItem = (index, next) =>
    setItems(section.items.map((it, i) => (i === index ? next : it)));

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h4 className="builder-subtitle m-0">Items ({section.items.length})</h4>
        {custom && (
          <div className="w-220px">
            <WeightMeter
              label="Item weights"
              total={itemWeightTotal(section)}
            />
          </div>
        )}
      </div>
      <SortableList
        items={section.items}
        onReorder={setItems}
        readOnly={readOnly}
        renderItem={(item, index, { handle, moveUp, moveDown }) => (
          <div className="builder-item">
            <div className="builder-item-head">
              {handle}
              <span className="builder-item-number">{index + 1}</span>
              <input
                className="form-control form-control-sm fw-semibold"
                value={item.title}
                placeholder="Item title, e.g. Financial Performance"
                aria-label={`Item ${index + 1} title`}
                onChange={(e) =>
                  setItem(
                    index,
                    withTitle(section.items, index, e.target.value),
                  )
                }
              />
              {custom && (
                <div className="input-group input-group-sm w-110px flex-shrink-0">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="form-control"
                    value={item.weight}
                    aria-label={`Weight of ${item.title || `item ${index + 1}`}`}
                    onChange={(e) =>
                      setItem(index, {
                        ...item,
                        weight: numValue(e.target.value),
                      })
                    }
                  />
                  <span className="input-group-text">%</span>
                </div>
              )}
              <RowTools
                moveUp={moveUp}
                moveDown={moveDown}
                onRemove={
                  section.items.length > 1
                    ? () =>
                        setItems(section.items.filter((_, i) => i !== index))
                    : null
                }
                label={item.title || `item ${index + 1}`}
                readOnly={readOnly}
              />
            </div>
            <div className="row g-2 mt-1">
              <div className="col-md-8">
                <textarea
                  className="form-control form-control-sm"
                  rows={1}
                  value={item.description}
                  placeholder="Description (optional)"
                  aria-label={`Item ${index + 1} description`}
                  onChange={(e) =>
                    setItem(index, { ...item, description: e.target.value })
                  }
                />
              </div>
              <div className="col-md-4">
                <KeyField
                  id={`${item._id}-key`}
                  element={item}
                  onChange={(next) => setItem(index, next)}
                  small
                />
              </div>
              <div className="col-12">
                <ChipsInput
                  id={`${item._id}-kpis`}
                  value={item.kpis}
                  onChange={(kpis) => setItem(index, { ...item, kpis })}
                  placeholder="KPIs: type one and press Enter"
                  readOnly={readOnly}
                />
              </div>
            </div>
          </div>
        )}
      />
      {!readOnly && (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary mt-2"
          onClick={() => setItems([...section.items, newItem()])}
        >
          <i className="bi bi-plus-lg"></i> Add item
        </button>
      )}
    </div>
  );
};

const RatingBody = ({ section, set, readOnly }) => (
  <>
    <div className="row g-3 mb-3">
      <div className="col-sm-4">
        <label htmlFor={`${section._id}-weight`} className="profile-label">
          Weight <span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            id={`${section._id}-weight`}
            type="number"
            min="0"
            max="100"
            className="form-control"
            value={section.weight}
            onChange={(e) => set({ weight: numValue(e.target.value) })}
          />
          <span className="input-group-text">% of total</span>
        </div>
      </div>
      <div className="col-sm-8">
        <span className="profile-label" id={`${section._id}-weighting`}>
          Item weighting
        </span>
        <div
          className="segmented"
          role="radiogroup"
          aria-labelledby={`${section._id}-weighting`}
        >
          {[
            ["EQUAL", "Equal"],
            ["CUSTOM", "Custom %"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={section.itemWeighting === value}
              className={`segmented-option ${section.itemWeighting === value ? "active" : ""}`}
              onClick={() => set({ itemWeighting: value })}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="col-sm-6">
        <Toggle
          id={`${section._id}-itemc`}
          checked={section.allowItemComments}
          onChange={(v) => set({ allowItemComments: v })}
          label="Per-item critical incidents"
        />
      </div>
      <div className="col-sm-6">
        <Toggle
          id={`${section._id}-secc`}
          checked={section.allowSectionComment}
          onChange={(v) => set({ allowSectionComment: v })}
          label="Section critical incidents"
        />
      </div>
    </div>
    <ScaleEditor section={section} set={set} readOnly={readOnly} />
    <RatingItems section={section} set={set} readOnly={readOnly} />
  </>
);

/* ---------- TEXT_LIST / TABLE / TEXT ---------- */
const TextListBody = ({ section, set }) => (
  <div className="row g-3">
    <div className="col-sm-3">
      <label htmlFor={`${section._id}-minE`} className="profile-label">
        Min entries
      </label>
      <input
        id={`${section._id}-minE`}
        type="number"
        min="0"
        className="form-control"
        value={section.minEntries}
        onChange={(e) => set({ minEntries: numValue(e.target.value) })}
      />
    </div>
    <div className="col-sm-3">
      <label htmlFor={`${section._id}-maxE`} className="profile-label">
        Max entries
      </label>
      <input
        id={`${section._id}-maxE`}
        type="number"
        min="1"
        className="form-control"
        value={section.maxEntries}
        onChange={(e) => set({ maxEntries: numValue(e.target.value) })}
      />
    </div>
  </div>
);

const TableBody = ({ section, set, readOnly }) => {
  const setColumns = (columns) => set({ columns });
  const setColumn = (index, next) =>
    setColumns(section.columns.map((c, i) => (i === index ? next : c)));
  return (
    <>
      <div className="row g-3 mb-2">
        <div className="col-sm-3">
          <label htmlFor={`${section._id}-rows`} className="profile-label">
            Max rows
          </label>
          <input
            id={`${section._id}-rows`}
            type="number"
            min="1"
            className="form-control"
            value={section.maxRows}
            onChange={(e) => set({ maxRows: numValue(e.target.value) })}
          />
        </div>
      </div>
      <h4 className="builder-subtitle">Columns</h4>
      <SortableList
        items={section.columns}
        onReorder={setColumns}
        readOnly={readOnly}
        renderItem={(col, index, { handle, moveUp, moveDown }) => (
          <div className="builder-item builder-item-head">
            {handle}
            <input
              className="form-control form-control-sm"
              value={col.label}
              placeholder="Column label, e.g. Action Plan"
              aria-label={`Column ${index + 1} label`}
              onChange={(e) =>
                setColumn(
                  index,
                  withTitle(section.columns, index, e.target.value, "label"),
                )
              }
            />
            <select
              className="form-select form-select-sm w-110px flex-shrink-0"
              value={col.type}
              onChange={(e) =>
                setColumn(index, { ...col, type: e.target.value })
              }
              aria-label={`Column ${index + 1} type`}
            >
              <option value="text">Text</option>
              <option value="date">Date</option>
            </select>
            <input
              className="form-control form-control-sm font-monospace w-140px flex-shrink-0"
              value={col.key}
              disabled={col._saved}
              aria-label={`Column ${index + 1} key`}
              title={
                col._saved ? "Keys can't change after the first save" : "Key"
              }
              onChange={(e) =>
                setColumn(index, {
                  ...col,
                  key: sanitizeKey(e.target.value),
                  _keyEdited: true,
                })
              }
            />
            <RowTools
              moveUp={moveUp}
              moveDown={moveDown}
              onRemove={
                section.columns.length > 1
                  ? () =>
                      setColumns(section.columns.filter((_, i) => i !== index))
                  : null
              }
              label={col.label || `column ${index + 1}`}
              readOnly={readOnly}
            />
          </div>
        )}
      />
      {!readOnly && (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary mt-2"
          onClick={() => setColumns([...section.columns, newColumn()])}
        >
          <i className="bi bi-plus-lg"></i> Add column
        </button>
      )}
    </>
  );
};

/**
 * One section card. `sections` + `index` are needed to keep keys unique.
 * onChange(nextSection); handle/moveUp/moveDown come from SortableList.
 */
const SectionEditor = ({
  sections,
  index,
  onChange,
  onRemove,
  collapsed,
  onToggle,
  handle,
  moveUp,
  moveDown,
  readOnly,
}) => {
  const section = sections[index];
  const set = (changes) => onChange({ ...section, ...changes });
  const isRating = section.type === "RATING";

  return (
    <div
      className={`builder-section builder-section--${section.type.toLowerCase()}`}
    >
      <div className="builder-section-head">
        {handle}
        <span className="section-type-chip">
          {SECTION_TYPE_LABEL[section.type]}
        </span>
        <button
          type="button"
          className="builder-section-toggle"
          onClick={onToggle}
          aria-expanded={!collapsed}
        >
          <span className="text-truncate">
            {section.title || (
              <span className="text-slate-400">Untitled section</span>
            )}
          </span>
          {isRating && section.weight !== "" && (
            <span className="section-weight-chip">{section.weight}%</span>
          )}
          <i
            className={`bi bi-chevron-${collapsed ? "down" : "up"} ms-auto`}
          ></i>
        </button>
        <RowTools
          moveUp={moveUp}
          moveDown={moveDown}
          onRemove={onRemove}
          label={section.title || "section"}
          readOnly={readOnly}
        />
      </div>

      {!collapsed && (
        <div className="builder-section-body">
          <div className="row g-3 mb-3">
            <div className="col-md-8">
              <label htmlFor={`${section._id}-title`} className="profile-label">
                Title <span className="text-danger">*</span>
              </label>
              <input
                id={`${section._id}-title`}
                className="form-control"
                value={section.title}
                placeholder={
                  isRating
                    ? "Part I – Key Result Areas (KRAs)"
                    : "Section title"
                }
                onChange={(e) =>
                  onChange(withTitle(sections, index, e.target.value))
                }
              />
            </div>
            <div className="col-md-4">
              <KeyField
                id={`${section._id}-key`}
                element={section}
                onChange={onChange}
              />
            </div>
            <div className="col-12">
              <label htmlFor={`${section._id}-desc`} className="profile-label">
                Description
              </label>
              <textarea
                id={`${section._id}-desc`}
                className="form-control"
                rows={1}
                value={section.description}
                onChange={(e) => set({ description: e.target.value })}
              />
            </div>
            {!isRating && (
              <div className="col-12">
                <Toggle
                  id={`${section._id}-req`}
                  checked={section.required}
                  onChange={(v) => set({ required: v })}
                  label="Required"
                />
              </div>
            )}
          </div>

          {isRating && (
            <RatingBody section={section} set={set} readOnly={readOnly} />
          )}
          {section.type === "TEXT_LIST" && (
            <TextListBody section={section} set={set} />
          )}
          {section.type === "TABLE" && (
            <TableBody section={section} set={set} readOnly={readOnly} />
          )}
        </div>
      )}
    </div>
  );
};

export default SectionEditor;
